import { NavLink, Link } from "@remix-run/react";
import type { LinksFunction } from "@vercel/remix";
import { SignedIn, SignedOut, UserButton, SignUpButton } from "@clerk/remix";
import Footer from "~/components/Footer";
import StructuredData from "~/components/StructuredData";
import BlackFridayBanner from "~/components/BlackFridayBanner";

export const links: LinksFunction = () => [
  { rel: "canonical", href: "https://cloudexamhub.com/glossary" },
];

// Add metadata export for Remix
export function meta() {
  return [
    { title: "AWS Cloud Practitioner Glossary 2026 — Key Terms & Definitions" },
    {
      name: "description",
      content:
        "Complete AWS glossary for Cloud Practitioner (CLF-C02) exam prep. Master essential AWS terms, services, and concepts from A to Z. Updated for 2026.",
    },
    {
      name: "keywords",
      content:
        "AWS glossary, Cloud Practitioner terms, AWS certification, cloud computing terms, AWS definitions, CLF-C02 glossary, AWS services glossary",
    },
    { property: "og:title", content: "AWS Cloud Practitioner Glossary 2026 — Key Terms & Definitions" },
    {
      property: "og:description",
      content:
        "Complete AWS glossary for Cloud Practitioner (CLF-C02) exam prep. Master essential AWS terms, services, and concepts from A to Z.",
    },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://cloudexamhub.com/glossary" },
    { property: "og:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:title", content: "AWS Cloud Practitioner Glossary 2026 — Key Terms & Definitions" },
    {
      name: "twitter:description",
      content:
        "Complete AWS glossary for Cloud Practitioner (CLF-C02) exam prep. Master essential AWS terms, services, and concepts from A to Z.",
    },
  ];
}

const GlossaryPage = () => {
  // Glossary data structure
  const glossaryData = {
    A: [
      {
        term: "Availability Zone (AZ)",
        definition:
          "A distinct physical location within an AWS region, consisting of one or more data centers. AZs provide redundancy and fault tolerance.",
      },
      {
        term: "Amazon EC2 (Elastic Compute Cloud)",
        definition:
          "A web service that provides resizable compute capacity in the cloud.",
      },
      {
        term: "Amazon S3 (Simple Storage Service)",
        definition:
          "Object storage service that allows for scalable data storage and retrieval.",
      },
      {
        term: "AWS Auto Scaling",
        definition:
          "A service that automatically adjusts the number of EC2 instances based on demand.",
      },
    ],
    B: [
      {
        term: "Billing Dashboard",
        definition:
          "The AWS console section where users can view billing details, set budgets, and track usage.",
      },
      {
        term: "Block Storage",
        definition:
          "A type of storage (e.g., Amazon EBS) that provides low-latency access to data, often used for databases and applications.",
      },
    ],
    C: [
      {
        term: "CloudFormation",
        definition:
          "Infrastructure as Code (IaC) service that allows you to define and provision AWS infrastructure using templates.",
      },
      {
        term: "CloudFront",
        definition:
          "AWS's global Content Delivery Network (CDN) that delivers data with low latency.",
      },
      {
        term: "CloudWatch",
        definition:
          "A monitoring and observability service for AWS resources, including logs, metrics, and alarms.",
      },
      {
        term: "Compliance Programs",
        definition:
          "AWS adheres to multiple compliance standards like ISO 27001, HIPAA, and PCI DSS to ensure security and compliance.",
      },
    ],
    D: [
      {
        term: "DynamoDB",
        definition:
          "A fully managed NoSQL database service known for low-latency, high-speed performance.",
      },
      {
        term: "Direct Connect",
        definition:
          "A service that establishes a dedicated network connection between your data center and AWS.",
      },
    ],
    E: [
      {
        term: "Elastic Load Balancing (ELB)",
        definition:
          "Distributes incoming traffic across multiple targets to enhance application availability.",
      },
      {
        term: "Elastic Beanstalk",
        definition:
          "A PaaS (Platform as a Service) offering that automatically manages deployment, scaling, and monitoring of applications.",
      },
    ],
    F: [
      {
        term: "Fault Tolerance",
        definition:
          "The ability of AWS services to continue running smoothly despite failures.",
      },
      {
        term: "Federated Access",
        definition:
          "Authentication method allowing users to sign in using existing credentials from external identity providers.",
      },
    ],
    G: [
      {
        term: "Glacier",
        definition:
          "A low-cost archive storage solution in Amazon S3, designed for long-term data retention.",
      },
      {
        term: "Global Accelerator",
        definition:
          "A networking service that routes traffic through AWS's global infrastructure for improved availability and performance.",
      },
    ],
    H: [
      {
        term: "High Availability (HA)",
        definition:
          "The design of AWS services to minimize downtime and ensure operational continuity.",
      },
      {
        term: "Hybrid Cloud",
        definition:
          "A mix of on-premises and AWS cloud services to optimize workload performance.",
      },
    ],
    I: [
      {
        term: "IAM (Identity and Access Management)",
        definition:
          "A service that enables users to control access to AWS resources securely.",
      },
      {
        term: "Instance",
        definition: "A virtual server running on AWS EC2.",
      },
      {
        term: "Infrastructure as Code (IaC)",
        definition:
          "A method of managing and provisioning AWS infrastructure using code-based automation (e.g., CloudFormation, Terraform).",
      },
    ],
    J: [
      {
        term: "JSON (JavaScript Object Notation)",
        definition:
          "A lightweight data-interchange format often used in AWS configurations and API responses.",
      },
    ],
    K: [
      {
        term: "Kinesis",
        definition:
          "A real-time data streaming service that collects, processes, and analyzes data in real time.",
      },
      {
        term: "Key Management Service (KMS)",
        definition:
          "AWS's managed service for creating and controlling encryption keys.",
      },
    ],
    L: [
      {
        term: "Lambda",
        definition:
          "A serverless computing service that executes code in response to events without managing infrastructure.",
      },
      {
        term: "Lightsail",
        definition:
          "A simplified cloud service that provides virtual private servers, databases, and networking.",
      },
    ],
    M: [
      {
        term: "Multi-AZ Deployment",
        definition:
          "A high-availability solution that replicates databases across multiple availability zones.",
      },
      {
        term: "Machine Learning (ML)",
        definition:
          "AWS provides AI/ML services like SageMaker to develop and deploy machine learning models.",
      },
    ],
    N: [
      {
        term: "NAT Gateway",
        definition:
          "A service that allows instances in a private subnet to access the internet securely.",
      },
      {
        term: "Network ACL (NACL)",
        definition:
          "A security feature that acts as a firewall at the subnet level.",
      },
    ],
    O: [
      {
        term: "Object Storage",
        definition:
          "A storage format used by Amazon S3 to store data as objects rather than blocks or files.",
      },
      {
        term: "On-Demand Pricing",
        definition:
          "A pricing model where customers pay for AWS services as they use them without long-term commitments.",
      },
    ],
    P: [
      {
        term: "PrivateLink",
        definition:
          "Enables private connectivity between AWS services and on-premises applications.",
      },
      {
        term: "Public Subnet",
        definition:
          "A subnet that is directly accessible from the internet via a public IP address.",
      },
    ],
    Q: [
      {
        term: "QuickSight",
        definition:
          "AWS's business intelligence tool for visualizing and analyzing data.",
      },
      {
        term: "Query Performance",
        definition:
          "Optimizing SQL queries in services like Redshift for better database efficiency.",
      },
    ],
    R: [
      {
        term: "RDS (Relational Database Service)",
        definition:
          "A managed database service supporting MySQL, PostgreSQL, SQL Server, and more.",
      },
      {
        term: "Route 53",
        definition:
          "AWS's scalable domain name system (DNS) web service for routing end-user traffic.",
      },
    ],
    S: [
      {
        term: "S3 Glacier",
        definition: "Low-cost, long-term data storage for archiving.",
      },
      {
        term: "Security Groups",
        definition:
          "Virtual firewalls that control inbound and outbound traffic for AWS resources.",
      },
      {
        term: "Serverless",
        definition:
          "A cloud-native model where infrastructure is managed automatically (e.g., AWS Lambda).",
      },
    ],
    T: [
      {
        term: "Terraform",
        definition:
          "An open-source infrastructure as code tool for provisioning AWS resources.",
      },
      {
        term: "Transit Gateway",
        definition:
          "A networking service that connects multiple VPCs and on-premises networks via a central hub.",
      },
    ],
    U: [
      {
        term: "Universal Naming Convention (UNC)",
        definition:
          "A naming format used for file shares in AWS storage services.",
      },
      {
        term: "User Pools",
        definition:
          "A feature in Amazon Cognito that provides user authentication and identity management.",
      },
    ],
    V: [
      {
        term: "VPC (Virtual Private Cloud)",
        definition:
          "A private cloud environment within AWS for networking and security configurations.",
      },
      {
        term: "VPN (Virtual Private Network)",
        definition:
          "A secure tunnel for encrypting network traffic between on-premises and AWS environments.",
      },
    ],
    W: [
      {
        term: "WAF (Web Application Firewall)",
        definition:
          "A security service that protects web applications from common threats like SQL injection and cross-site scripting (XSS).",
      },
      {
        term: "Well-Architected Framework",
        definition:
          "AWS's best practices guide for designing secure, high-performing cloud architectures.",
      },
    ],
    X: [
      {
        term: "X-Ray",
        definition:
          "AWS's distributed tracing tool for monitoring and debugging applications.",
      },
    ],
    Z: [
      {
        term: "Zonal Shift",
        definition:
          "A feature that allows rerouting traffic from one Availability Zone to another during outages.",
      },
    ],
  };

  // Updated Schema for SEO with all terms
  const glossarySchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: Object.entries(glossaryData).flatMap(([, terms]) =>
      terms.map(({ term, definition }) => ({
        "@type": "Question",
        name: `What is ${term} in AWS?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: definition,
        },
      }))
    ),
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://cloudexamhub.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "AWS Cloud Practitioner Glossary",
        item: "https://cloudexamhub.com/glossary",
      },
    ],
  };

  return (
    <div className="flex flex-col min-h-screen">
      <StructuredData data={glossarySchema} />
      <StructuredData data={breadcrumbSchema} />
      <BlackFridayBanner />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b px-4 sm:px-8 lg:px-12 h-16 sm:h-14 flex items-center">
        <NavLink to="/" className="flex items-center justify-center">
          <img
            src="/cloud-check.png"
            alt="Cloud Exam Hub"
            className="h-8 w-8 sm:h-6 sm:w-6"
          />
          <span className="ml-2 font-bold text-lg sm:text-xl truncate">
            Cloud Exam Hub
          </span>
        </NavLink>
        <nav className="ml-auto flex gap-4 sm:gap-4 items-center">
          <SignedIn>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md text-sm transition-colors">
                Get Practice Exams
              </button>
            </SignUpButton>
          </SignedOut>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="w-full py-20 md:py-28 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl mb-6">
              AWS Cloud Practitioner Glossary 2026
            </h1>
            <h2 className="max-w-[800px] mx-auto text-gray-200 text-lg md:text-xl">
              Master essential AWS terms and concepts for the CLF-C02 exam
            </h2>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 mb-8">
                Welcome to the ultimate AWS glossary, designed for those
                preparing for the
                <strong>
                  {" "}
                  AWS Certified Cloud Practitioner (CCP) exam
                </strong>{" "}
                and anyone looking to understand key AWS concepts. This page
                covers fundamental AWS terms to help you navigate the cloud
                ecosystem.
              </p>

              {/* Quick Navigation */}
              <nav className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Quick Navigation
                </h2>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(glossaryData).map((letter) => (
                    <a
                      key={letter}
                      href={`#section-${letter.toLowerCase()}`}
                      className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                    >
                      {letter}
                    </a>
                  ))}
                </div>
              </nav>

              {/* Glossary Sections */}
              {Object.entries(glossaryData).map(([letter, terms]) => (
                <section key={letter} className="mb-12">
                  <h2
                    className="text-2xl font-bold text-gray-800 mb-6 pt-4 border-t"
                    id={`section-${letter.toLowerCase()}`}
                  >
                    {letter}
                  </h2>
                  <div className="space-y-6">
                    {terms.map(({ term, definition }, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-6 transition-all hover:shadow-md"
                      >
                        <h3 className="font-bold text-gray-900 mb-2">{term}</h3>
                        <p className="text-gray-700">{definition}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              {/* Call to Action */}
              <div className="mt-12 p-8 bg-blue-50 border border-blue-200 rounded-xl text-center">
                <h2 className="text-xl font-bold text-blue-900 mb-3">
                  Knowing the Terms Is Just the Start
                </h2>
                <p className="text-blue-800 mb-6">
                  The CLF-C02 exam will test how well you can apply these concepts — not just define them. Cloud Exam Hub&apos;s 500+ practice questions put these terms in real exam scenarios so you&apos;re ready for anything on test day.
                </p>
                <SignedOut>
                  <SignUpButton mode="modal">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors">
                      Start Practicing for $11.99 →
                    </button>
                  </SignUpButton>
                  <p className="mt-3 text-sm text-blue-700">One-time payment · 14-day money-back guarantee</p>
                </SignedOut>
                <SignedIn>
                  <Link
                    to="/user-home"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors inline-block"
                  >
                    Go to Practice Exams →
                  </Link>
                </SignedIn>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Conversion Section */}
      <section className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 py-16 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Pass the CLF-C02?</h2>
          <p className="text-lg text-blue-100 mb-8">
            You&apos;ve studied the terms — now test yourself under real exam conditions. Cloud Exam Hub gives you 500+ up-to-date practice questions with detailed explanations for every answer.
          </p>
          <div className="bg-white/10 rounded-xl p-6 mb-8 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold">500+</div>
              <div className="text-blue-100">Practice questions</div>
            </div>
            <div>
              <div className="text-2xl font-bold">Unlimited</div>
              <div className="text-blue-100">Exam simulations</div>
            </div>
            <div>
              <div className="text-2xl font-bold">14-day</div>
              <div className="text-blue-100">Money-back guarantee</div>
            </div>
          </div>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg">
                Get Access for $11.99 →
              </button>
            </SignUpButton>
            <p className="mt-3 text-blue-200 text-sm">One-time payment · No subscription · Was $19.99</p>
          </SignedOut>
          <SignedIn>
            <Link
              to="/user-home"
              className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg inline-block"
            >
              Go to Practice Exams →
            </Link>
          </SignedIn>
        </div>
      </section>

      {/* Related Resources */}
      <section className="container mx-auto px-4 py-10 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to="/certification-faqs" className="text-blue-600 hover:text-blue-700 font-medium">
                AWS Cloud Practitioner Certification FAQs 2026
              </Link>
            </li>
            <li>
              <Link to="/checklist" className="text-blue-600 hover:text-blue-700 font-medium">
                AWS Cloud Practitioner 4-Week Study Checklist
              </Link>
            </li>
            <li>
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                Cloud Exam Hub — Practice Exams
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GlossaryPage;
